import { Dispatch, SetStateAction, forwardRef, useState } from "react";
import { Box, Grid2, Skeleton, TextField } from "@mui/material";
import { FilterList } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { IFloorPayload } from "../../FacilityManager/Domain/Types/FloorPlan/FloorPayload.type";
import { GroupFiltersSub } from "./GroupFiltersSub.partial";
import { GroupFilterSelected, GroupFilterType } from "./types/GroupFilters.type";
import { IPlaceCategory } from "../../FacilityManager/Domain/Types/FloorPlan/PlaceCategory.type";
import { IZoneCategory } from "../../FacilityManager/Domain/Types/FloorPlan/ZoneCategory.type";
import { EquipmentCategory } from "../../FacilityManager/Domain/Types/FloorPlan/EquipmentCategory.type";
import { BookingProperty } from "../../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import { BookingFilterMode } from "../Domain/booking-filter-mode-enum.type";

type P = {
  floorPlan: IFloorPayload | undefined;
  showDetailGroupFilter: boolean;
  filterItems: GroupFilterSelected[];
  setFilterItems: Dispatch<SetStateAction<GroupFilterSelected[]>>;
  usedCategory: { type: "zone" | "place"; options: (IPlaceCategory | IZoneCategory)[] };
  usedOptions:
    | {
        usedEquipment: { id: number; name: string }[];
        usedEquipmentCategories: (EquipmentCategory & { quantity: number })[];
        usedBookingProperties: BookingProperty[];
      }
    | undefined;
  filterMode: BookingFilterMode;
  isCapacityReportUsed: boolean;
};

export const GroupFilters: React.FC<P> = forwardRef<HTMLDivElement, P>((props, ref) => {
  const { t } = useTranslation();
  const {
    floorPlan,
    showDetailGroupFilter,
    filterItems,
    usedCategory,
    usedOptions,
    filterMode,
    isCapacityReportUsed,
    setFilterItems
  } = props;

  const [filterName, setFilterName] = useState<string>("");

  // each filter type collapse state
  const [moreEquipment, setMoreEquipment] = useState(false);
  const [moreEquipCate, setMoreEquipCate] = useState(false);
  const [moreBookProp, setMoreBookProp] = useState(false);
  const [morePlaceCate, setMorePlaceCate] = useState(false);
  const [moreZoneCate, setMoreZoneCate] = useState(false);

  return (
    <Box mt={1} width={"100%"} ref={ref}>
      <Grid2 sx={{ mb: 4 }}>
        <TextField
          data-testid="booking-group-filter-field"
          fullWidth
          disabled={!floorPlan}
          placeholder={t("Filter")}
          slotProps={{ input: { startAdornment: <FilterList sx={{ mr: 1 }} /> } }}
          onChange={event => setFilterName(event.target.value.toLowerCase())}
        />
      </Grid2>

      {/* awaiting the floor plan response */}
      {!floorPlan && (
        <Grid2 mt={2}>
          <Skeleton />
          <Skeleton />
        </Grid2>
      )}

      {floorPlan && (
        <Grid2 container data-testid="booking-group-filter-container">
          {/* individual equipment */}
          <GroupFiltersSub
            type={GroupFilterType.EQUIPMENT}
            toggleMoreItems={moreEquipment}
            setToggleMoreItems={setMoreEquipment}
            entries={usedOptions?.usedEquipment}
            showDetailGroupFilter={showDetailGroupFilter}
            filterName={filterName}
            filterItems={filterItems}
            filterMode={filterMode}
            setFilterItems={setFilterItems}
            isCapacityReportUsed={isCapacityReportUsed}
          />

          {/* equipment categories */}
          <GroupFiltersSub
            type={GroupFilterType.EQUIPMENTCATEGORY}
            toggleMoreItems={moreEquipCate}
            setToggleMoreItems={setMoreEquipCate}
            entries={usedOptions?.usedEquipmentCategories}
            showDetailGroupFilter={showDetailGroupFilter}
            filterName={filterName}
            filterItems={filterItems}
            filterMode={filterMode}
            setFilterItems={setFilterItems}
            isCapacityReportUsed={isCapacityReportUsed}
          />

          {/* booking properties */}
          <GroupFiltersSub
            type={GroupFilterType.BOOKINGPROPERTY}
            toggleMoreItems={moreBookProp}
            setToggleMoreItems={setMoreBookProp}
            entries={usedOptions?.usedBookingProperties}
            showDetailGroupFilter={showDetailGroupFilter}
            filterName={filterName}
            filterItems={filterItems}
            filterMode={filterMode}
            setFilterItems={setFilterItems}
            isCapacityReportUsed={isCapacityReportUsed}
          />

          {/* place categories */}
          <GroupFiltersSub
            type={GroupFilterType.PLACECATEGORY}
            toggleMoreItems={morePlaceCate}
            setToggleMoreItems={setMorePlaceCate}
            entries={usedCategory?.type === "place" ? usedCategory.options : []}
            showDetailGroupFilter={showDetailGroupFilter}
            filterName={filterName}
            filterItems={filterItems}
            filterMode={filterMode}
            setFilterItems={setFilterItems}
            isCapacityReportUsed={isCapacityReportUsed}
          />

          {/* zone categories */}
          <GroupFiltersSub
            type={GroupFilterType.ZONECATEGORY}
            toggleMoreItems={moreZoneCate}
            setToggleMoreItems={setMoreZoneCate}
            entries={usedCategory?.type === "zone" ? usedCategory.options : []}
            showDetailGroupFilter={showDetailGroupFilter}
            filterName={filterName}
            filterItems={filterItems}
            filterMode={filterMode}
            setFilterItems={setFilterItems}
            isCapacityReportUsed={isCapacityReportUsed}
          />

          {/* end of comp */}
        </Grid2>
      )}
    </Box>
  );
});
